import { useState, useEffect } from 'react';
import { Building2, Loader2, AlertTriangle, MailCheck, MailWarning, LifeBuoy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getMyBilling } from '@/services/apiClient';

/**
 * The billing identity that will appear on this company's invoices.
 *
 * Read-only. Shown so a customer can check the legal name and GSTIN before an
 * invoice is issued rather than discovering an error on a PDF that has already
 * been raised — a wrong GSTIN is their problem more than ours, since they are
 * the ones who cannot claim input credit against it.
 */

const LABELS = [
    ['legalName', 'Registered name'],
    ['gstin', 'GSTIN'],
    ['pan', 'PAN'],
    ['billingEmail', 'Billing email'],
    ['billingPhone', 'Phone'],
];

export default function MyBillingPanel() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        getMyBilling()
            .then(res => setData(res.data))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, []);

    if (loading) {
        return (
            <div className="flex h-28 items-center justify-center rounded-xl border border-border bg-card">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
            </div>
        );
    }
    if (!data) return null;

    const b = data.billing || {};
    const missing = data.missing || [];
    const address = [b.addressLine1, b.addressLine2, [b.city, b.state, b.pincode].filter(Boolean).join(', '), b.country]
        .filter(Boolean);
    const anythingSet = LABELS.some(([k]) => b[k]) || address.length > 0;

    return (
        <div className="rounded-xl border border-border bg-card p-5">
            <div className="mb-1 flex items-center gap-2">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                <h2 className="font-semibold text-foreground">Billing details</h2>
            </div>
            <p className="mb-4 text-sm text-muted-foreground">
                What will appear on your invoices. Managed by your account manager —
                tell them if anything here is wrong.
            </p>

            {missing.length > 0 && (
                <div className="mb-4 flex items-start gap-2.5 rounded-lg border border-amber-500/40 bg-amber-500/5 p-3">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-500" />
                    <p className="text-sm text-foreground">
                        Your account manager still needs to add:{' '}
                        <strong>{missing.join(', ')}</strong>. Invoices cannot be raised until then.
                    </p>
                </div>
            )}

            {anythingSet ? (
                <dl className="grid gap-x-8 gap-y-2.5 text-sm sm:grid-cols-2">
                    {LABELS.filter(([k]) => b[k]).map(([k, label]) => (
                        <div key={k} className="flex justify-between gap-3 border-b border-border/60 pb-2">
                            <dt className="text-muted-foreground">{label}</dt>
                            <dd className={`text-right font-medium ${k === 'gstin' || k === 'pan' ? 'font-mono text-xs' : ''}`}>
                                {b[k]}
                                {k === 'billingEmail' && (
                                    <span className={`mt-0.5 flex items-center justify-end gap-1 text-[11px] font-normal ${
                                        b.billingEmailVerified
                                            ? 'text-emerald-600 dark:text-emerald-500'
                                            : 'text-amber-600 dark:text-amber-500'
                                    }`}>
                                        {b.billingEmailVerified
                                            ? <><MailCheck className="h-3 w-3" /> confirmed</>
                                            : <><MailWarning className="h-3 w-3" /> not confirmed yet</>}
                                    </span>
                                )}
                            </dd>
                        </div>
                    ))}
                    {address.length > 0 && (
                        <div className="flex justify-between gap-3 border-b border-border/60 pb-2 sm:col-span-2">
                            <dt className="text-muted-foreground">Registered address</dt>
                            <dd className="text-right font-medium">
                                {address.map(line => <span key={line} className="block">{line}</span>)}
                            </dd>
                        </div>
                    )}
                </dl>
            ) : (
                <p className="py-4 text-center text-sm text-muted-foreground">
                    No billing details on file yet. Your account manager adds these before
                    the first invoice.
                </p>
            )}

            <div className="mt-4 flex justify-end">
                <Button
                    size="sm" variant="outline" className="gap-1.5 text-xs"
                    onClick={() => window.location.assign('/admin/support')}
                >
                    <LifeBuoy className="h-3.5 w-3.5" /> Report an error
                </Button>
            </div>
        </div>
    );
}
